import { Stack, Typography, Button, useTheme } from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode, useState } from "react";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { PatchContract } from "@/services/fhir/contract/PatchContract";
import { toast } from "react-toastify";
import { Description } from "@mui/icons-material";
import { ConfigureAIModal } from "./ConfigureAIModal";
import { CancelIntegrationModal } from "./CancelIntegrationModal";
import { useSessionContext } from "@/contexts/SessionContext";
import { ActivationInfoBlock } from "./ActivationInfoBlock";
import { getActivationResponsible, getProviderSlugFromContract } from "../helpers/activationInfo";
import { getProviderDisplayLabel } from "../helpers/aiProviderDisplay";

interface MedicalReportsAgentCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

export function MedicalReportsAgentCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: MedicalReportsAgentCardProps) {
  const { access } = useSessionContext();
  const theme = useTheme();
  const { contractMedicalReports, provider, refetchContract } =
    useMedicalReportsAgentContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleRemoveContract = async () => {
    await PatchContract({
      contractId: contractMedicalReports?.id,
      operation: "replace",
      path: "/status",
      content: "cancelled",
    });
    toast.success("Integração cancelada com sucesso");
    refetchContract();
    setIsModalOpen(false);
    setIsCancelModalOpen(false);
  };

  const openCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}
        
        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 3,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {contractMedicalReports && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <ActivationInfoBlock
                responsible={getActivationResponsible(
                  contractMedicalReports,
                  "medical_reports_agent"
                )}
                providerLabel={getProviderDisplayLabel(
                  getProviderSlugFromContract(
                    contractMedicalReports,
                    "medical_reports_agent"
                  ) || provider
                )}
              />
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractMedicalReports ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes('owner') ? "error700.main" : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": { backgroundColor: access?.roles?.includes('owner') ? "error700.main" : "neutral300.main" },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!access?.roles?.includes('owner')}
              onClick={openCancelModal}
            >
              {access?.roles?.includes('owner') ? "Cancelar Agente de Relatórios" : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={() => handleOpenModal()}
              startIcon={<Description />}
              disabled={!access?.roles?.includes('owner')}
            >
             {access?.roles?.includes('owner') ? "Ativar Agente de Relatórios" : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>
      <ConfigureAIModal
        open={isModalOpen}
        onClose={handleCloseModal}
        identifier="MEDICAL_REPORTS_AGENT"
      />
      <CancelIntegrationModal
        open={isCancelModalOpen}
        onClose={closeCancelModal}
        onConfirm={handleRemoveContract}
        agentName="Agente de Relatórios"
      />
    </>
  );
} 